// User column layout of a grid table: the order of the columns and the widths
// the user dragged them to. Kept apart from the Head (`head_${Name}` stores
// visibility only and is read directly by legacy pages) and applied at render
// time, so columns that appear or vanish with permissions, brand extensions or
// table settings simply fall in or out of the layout.
//
// Persistence goes through a store adapter. The default keeps the layout in
// this browser's localStorage; a backend-backed store (per user) can replace
// it later through the override registry without touching the grid, since
// the grid treats every store call as possibly asynchronous.

/**
 * @typedef {Object} GridColumnLayout
 * @property {1} v - Format version.
 * @property {string[]} order - Column ids in the user's order. Empty until the
 *   user moves a column; columns missing from it keep their default place.
 * @property {Object<string, number>} widths - Column id → width in px.
 */

/**
 * @typedef {Object} GridColumnLayoutStore
 * @property {(name: string) => (GridColumnLayout|null|Promise<GridColumnLayout|null>)} load
 * @property {(name: string, layout: GridColumnLayout) => (void|Promise<void>)} save
 * @property {(name: string) => (void|Promise<void>)} clear
 */

/**
 * @typedef {Object} LayoutColumn
 * @property {string} id - Stable column id (Head `id`).
 * @property {number} [width] - Width to start from when the user sized other
 *   columns but never this one.
 * @property {number} [minWidth] - Narrowest width the user may drag it to.
 * @property {number} [maxWidth] - Widest width the user may drag it to.
 */

export const GRID_COLUMN_LAYOUT_VERSION = 1;
export const GRID_COLUMN_MIN_WIDTH = 64;
export const GRID_COLUMN_MAX_WIDTH = 800;
// Width of a column that has none stored yet (shown after the others were
// sized) and declares no Head `width` of its own.
export const GRID_COLUMN_DEFAULT_WIDTH = 150;
// Widest a column gets when fitted to its content (double-click on its
// edge): one long value should not take the whole table.
export const GRID_COLUMN_AUTO_FIT_MAX_WIDTH = 480;

/** @type {GridColumnLayout} */
export const EMPTY_COLUMN_LAYOUT = Object.freeze({
    v: GRID_COLUMN_LAYOUT_VERSION,
    order: Object.freeze([]),
    widths: Object.freeze({})
});

const STORAGE_PREFIX = 'gridLayout_';

const isValidWidth = (value) =>
    typeof value === 'number' && Number.isFinite(value) && value > 0;

/**
 * Brings anything read from a store to a valid layout; an unknown version or
 * shape yields the empty layout rather than a half-applied one.
 * @param {*} raw - Whatever the store returned.
 * @returns {GridColumnLayout} a valid layout
 */
export function normalizeColumnLayout(raw) {
    if (!raw || typeof raw !== 'object' || raw.v !== GRID_COLUMN_LAYOUT_VERSION) {
        return EMPTY_COLUMN_LAYOUT;
    }
    const order = Array.isArray(raw.order)
        ? [...new Set(raw.order.filter((id) => typeof id === 'string'))]
        : [];
    const widths = {};
    if (raw.widths && typeof raw.widths === 'object') {
        Object.entries(raw.widths).forEach(([id, width]) => {
            if (isValidWidth(width)) widths[id] = Math.round(width);
        });
    }
    return { v: GRID_COLUMN_LAYOUT_VERSION, order, widths };
}

/**
 * @param {GridColumnLayout} layout - Layout to test.
 * @returns {boolean} true when the layout changes nothing
 */
export function isEmptyColumnLayout(layout) {
    return (
        !layout ||
        (layout.order.length === 0 && Object.keys(layout.widths).length === 0)
    );
}

/**
 * The default store: localStorage of this browser, one key per grid. Storage
 * errors (quota, private mode) are swallowed — the layout then lasts until
 * the page is left.
 * @type {GridColumnLayoutStore}
 */
export const localColumnLayoutStore = {
    load(name) {
        try {
            const raw = localStorage.getItem(STORAGE_PREFIX + name);
            return raw ? JSON.parse(raw) : null;
        } catch (e) {
            return null;
        }
    },
    save(name, layout) {
        try {
            if (isEmptyColumnLayout(layout)) {
                localStorage.removeItem(STORAGE_PREFIX + name);
            } else {
                localStorage.setItem(STORAGE_PREFIX + name, JSON.stringify(layout));
            }
        } catch (e) {
            // ignore
        }
    },
    clear(name) {
        try {
            localStorage.removeItem(STORAGE_PREFIX + name);
        } catch (e) {
            // ignore
        }
    }
};

/**
 * Reads a grid's layout through a store, sync or async alike. Never rejects.
 * @param {GridColumnLayoutStore} store - Where layouts are kept.
 * @param {string} name - Grid name (`this.Name`).
 * @returns {Promise<GridColumnLayout>} the stored layout, or the empty one
 */
export function loadColumnLayout(store, name) {
    return Promise.resolve()
        .then(() => store.load(name))
        .then(normalizeColumnLayout)
        .catch(() => EMPTY_COLUMN_LAYOUT);
}

/**
 * Writes a grid's layout through a store. Never rejects.
 * @param {GridColumnLayoutStore} store - Where layouts are kept.
 * @param {string} name - Grid name (`this.Name`).
 * @param {GridColumnLayout} layout - Layout to keep.
 * @returns {Promise<void>} settles once the store is done
 */
export function saveColumnLayout(store, name, layout) {
    return Promise.resolve()
        .then(() => store.save(name, layout))
        .catch(() => {});
}

/**
 * Removes a grid's layout through a store. Never rejects.
 * @param {GridColumnLayoutStore} store - Where layouts are kept.
 * @param {string} name - Grid name (`this.Name`).
 * @returns {Promise<void>} settles once the store is done
 */
export function clearColumnLayout(store, name) {
    return Promise.resolve()
        .then(() => store.clear(name))
        .catch(() => {});
}

/**
 * Orders columns by the layout. Columns the order does not know (added to the
 * Head later, or just made available) keep their default place: each goes
 * right after the column that precedes it in the default order.
 * @template {LayoutColumn} T
 * @param {T[]} columns - Columns in the default (Head) order.
 * @param {string[]} order - Layout order.
 * @returns {T[]} a new array; the input is returned as is when there is no order
 */
export function applyColumnOrder(columns, order) {
    if (!order || order.length === 0) return columns;

    const rank = new Map(order.map((id, index) => [id, index]));
    const result = columns
        .filter((column) => rank.has(column.id))
        .sort((a, b) => rank.get(a.id) - rank.get(b.id));

    columns.forEach((column, index) => {
        if (rank.has(column.id)) return;
        const at =
            index === 0 ? 0 : result.indexOf(columns[index - 1]) + 1;
        result.splice(at, 0, column);
    });

    return result;
}

/**
 * The order after dropping one column next to another.
 * @param {LayoutColumn[]} columns - All columns in the default order (hidden
 *   ones included, so they keep their place when shown again).
 * @param {string[]} order - Current layout order.
 * @param {string} fromId - The column being moved.
 * @param {string} toId - The column it was dropped on.
 * @param {'before'|'after'} side - Which side of `toId` it lands on.
 * @returns {string[]} the new order; the current one when the move is a no-op
 */
export function moveColumn(columns, order, fromId, toId, side) {
    const ids = applyColumnOrder(columns, order).map((column) => column.id);
    if (fromId === toId || !ids.includes(fromId) || !ids.includes(toId)) {
        return order;
    }
    const rest = ids.filter((id) => id !== fromId);
    const target = rest.indexOf(toId) + (side === 'after' ? 1 : 0);
    rest.splice(target, 0, fromId);
    return rest;
}

/**
 * Keeps a width within the column's limits (global ones by default).
 * @param {number} width - Requested width in px.
 * @param {LayoutColumn} [column] - Column whose own limits apply.
 * @returns {number} whole pixels
 */
export function clampColumnWidth(width, column = {}) {
    const min = column.minWidth ?? GRID_COLUMN_MIN_WIDTH;
    const max = Math.max(column.maxWidth ?? GRID_COLUMN_MAX_WIDTH, min);
    return Math.round(Math.min(Math.max(width, min), max));
}

/**
 * @param {GridColumnLayout} layout - Current layout.
 * @param {Object<string, number>} widths - Column id → width; clamped by the caller.
 * @returns {GridColumnLayout} a new layout with these widths merged in
 */
export function setColumnWidths(layout, widths) {
    return { ...layout, widths: { ...layout.widths, ...widths } };
}

/**
 * Drops ids of columns the grid no longer has, so a layout does not keep
 * growing with columns removed from the Head over time.
 * @param {GridColumnLayout} layout - Current layout.
 * @param {string[]} knownIds - Ids of every column of the grid's Head.
 * @returns {GridColumnLayout} the same layout when nothing was dropped
 */
export function pruneColumnLayout(layout, knownIds) {
    const known = new Set(knownIds);
    const order = layout.order.filter((id) => known.has(id));
    const widthIds = Object.keys(layout.widths);
    const keptWidthIds = widthIds.filter((id) => known.has(id));
    if (
        order.length === layout.order.length &&
        keptWidthIds.length === widthIds.length
    ) {
        return layout;
    }
    const widths = {};
    keptWidthIds.forEach((id) => {
        widths[id] = layout.widths[id];
    });
    return { ...layout, order, widths };
}
